// Simulates a consolidated route's timeline and flags time-window conflicts.
//
// Supports two modes:
// 1. Haversine fallback (default): straight-line distance / fixed avg speed.
// 2. HERE Matrix mode: caller pre-fetches a real travel-time + distance matrix
//    (see `collectUniquePoints` + `hereApi.fetchTravelMatrix`) and passes a
//    `travelProvider(fromLat, fromLng, toLat, toLng)` in opts.
//    Using the matrix gives road-accurate km and time (with traffic if requested).

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => d * Math.PI / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Collect every distinct pickup + delivery location across the ordered OTs,
// rounded to ~11 m so near-duplicates collapse. Returns the unique points and
// a lookup from (lat,lng) → index for the matrix.
function collectUniquePoints(orders) {
  const points = [];
  const indexByKey = new Map();
  const key = (lat, lng) => `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
  const add = (lat, lng) => {
    if (!lat || !lng) return;
    const k = key(lat, lng);
    if (indexByKey.has(k)) return;
    indexByKey.set(k, points.length);
    points.push({ lat: Number(lat), lng: Number(lng) });
  };
  for (const o of orders) {
    add(Number(o.pickupLat || o.pickup?.lat), Number(o.pickupLng || o.pickup?.lng));
    add(Number(o.deliveryLat || o.delivery?.lat), Number(o.deliveryLng || o.delivery?.lng));
  }
  const lookup = (lat, lng) => indexByKey.get(key(lat, lng));
  return { points, lookup };
}

// Build a travelProvider closure that reads seconds + metres from a HERE matrix,
// with Haversine fallback when a pair isn't in the matrix (shouldn't happen, but safe).
function makeMatrixProvider(matrix, lookup, avgSpeedKmh) {
  return (lat1, lng1, lat2, lng2) => {
    const i = lookup(lat1, lng1);
    const j = lookup(lat2, lng2);
    if (i != null && j != null) {
      const s = matrix.travelTimes[i][j];
      const m = matrix.distances[i][j];
      if (s > 0 || (i === j)) return { seconds: s, km: m / 1000 };
    }
    const km = haversineKm(lat1, lng1, lat2, lng2);
    return { seconds: (km / avgSpeedKmh) * 3600, km };
  };
}

function pickupCoords(o) {
  return {
    lat: Number(o.pickupLat || o.pickup?.lat),
    lng: Number(o.pickupLng || o.pickup?.lng),
    name: o.pickupName || o.pickupAddress || o.pickup?.name || '',
  };
}
function deliveryCoords(o) {
  return {
    lat: Number(o.deliveryLat || o.delivery?.lat),
    lng: Number(o.deliveryLng || o.delivery?.lng),
    name: o.deliveryName || o.deliveryAddress || o.delivery?.name || '',
  };
}

/**
 * Simulate the consolidated route.
 * @param {Array} orders           — OTs in the intended execution order (caller decides sort)
 * @param {Object} opts
 * @param {number} opts.avgSpeedKmh   average road speed (default 60)
 * @param {number} opts.defaultLoadMin  loading minutes when OT has no explicit value
 * @param {number} opts.defaultUnloadMin unloading minutes
 * @returns {{ timeline, conflicts, hasConflicts }}
 */
function simulate(orders, opts = {}) {
  const avgSpeedKmh = opts.avgSpeedKmh || 60;
  const loadDefault = opts.defaultLoadMin || 15;
  const unloadDefault = opts.defaultUnloadMin || 30;
  // travelProvider returns { seconds, km } for any segment. When not provided,
  // fall back to Haversine at the fixed average speed.
  const travelProvider = opts.travelProvider
    || ((lat1, lng1, lat2, lng2) => {
      const km = haversineKm(lat1, lng1, lat2, lng2);
      return { seconds: (km / avgSpeedKmh) * 3600, km };
    });

  const timeline = [];
  const conflicts = [];
  if (!Array.isArray(orders) || orders.length === 0) {
    return { timeline, conflicts, hasConflicts: false };
  }

  const first = orders[0];
  // Start time: first pickup-window-from, or delivery-window-from as fallback, or now
  const startMs = new Date(
    first.pickupWindowFrom || first.deliveryWindowFrom || Date.now()
  ).getTime();

  let t = startMs;
  // driverHome: optional {lat, lng} — when provided, the route starts at the
  // driver's home and travels to the first pickup before loading. Used for
  // assistance workflow where the technician leaves home in the morning.
  const driverHome = opts.driverHome;
  let curLat, curLng;
  if (driverHome?.lat && driverHome?.lng) {
    curLat = Number(driverHome.lat);
    curLng = Number(driverHome.lng);
  } else {
    const firstPickup = pickupCoords(first);
    curLat = firstPickup.lat;
    curLng = firstPickup.lng;
  }

  // Detect "shared pickup" runs — typical for assistance routes (every OT
  // picks up at the technician's home) and warehouse-to-many-clients runs.
  // When the pickup is the same for every OT we don't go back to it between
  // deliveries: the route is home → delivery1 → delivery2 → ... → home,
  // matching what handleConsolidateRoutes builds at the planner level.
  // Without this, the validator falsely reports "late pickup" because it
  // assumes a pickup→delivery alternation that doesn't happen in reality.
  const allSamePickup = (() => {
    const fp = pickupCoords(first);
    if (!fp.lat || !fp.lng) return false;
    return orders.every(o => {
      const p = pickupCoords(o);
      return p.lat && p.lng && isSamePoint(p.lat, p.lng, fp.lat, fp.lng);
    });
  })();
  const skipIntermediatePickups = allSamePickup || !!(driverHome?.lat && driverHome?.lng);

  // Event 0: initial pickup. If driverHome was provided, travel to it first.
  const firstPk = pickupCoords(first);
  if (driverHome?.lat && driverHome?.lng && firstPk.lat && firstPk.lng
      && !isSamePoint(curLat, curLng, firstPk.lat, firstPk.lng)) {
    const leg = travelProvider(curLat, curLng, firstPk.lat, firstPk.lng);
    t += leg.seconds * 1000;
    curLat = firstPk.lat;
    curLng = firstPk.lng;
  }
  pushEvent(first, 'pickup', t, 'ok');
  // Load time at first pickup
  t += (first.loadingTime || loadDefault) * 60_000;

  for (let i = 0; i < orders.length; i++) {
    const ot = orders[i];
    const pk = pickupCoords(ot);
    const dl = deliveryCoords(ot);

    // For 2nd+ OT, travel back to its pickup ONLY when the route actually
    // requires it (different pickup location for each OT). For shared-pickup
    // runs (assistance / warehouse) we go straight from the previous delivery
    // to the next one — same as the actual route geometry.
    if (i > 0 && !skipIntermediatePickups && pk.lat && pk.lng) {
      const sameAsPrev = isSamePoint(curLat, curLng, pk.lat, pk.lng);
      if (!sameAsPrev) {
        const leg = travelProvider(curLat, curLng, pk.lat, pk.lng);
        t += leg.seconds * 1000;
        curLat = pk.lat;
        curLng = pk.lng;
        const status = checkWindow(t, ot.pickupWindowFrom, ot.pickupWindowTo);
        pushEvent(ot, 'pickup', t, status);
        // If we arrive before the window, wait
        if (status === 'early' && ot.pickupWindowFrom) {
          t = new Date(ot.pickupWindowFrom).getTime();
        }
        t += (ot.loadingTime || loadDefault) * 60_000;
      }
    }

    // Travel to delivery
    if (dl.lat && dl.lng) {
      const leg = travelProvider(curLat, curLng, dl.lat, dl.lng);
      t += leg.seconds * 1000;
      curLat = dl.lat;
      curLng = dl.lng;
      const status = checkWindow(t, ot.deliveryWindowFrom, ot.deliveryWindowTo);
      pushEvent(ot, 'delivery', t, status);
      if (status === 'early' && ot.deliveryWindowFrom) {
        t = new Date(ot.deliveryWindowFrom).getTime();
      }
      t += (ot.unloadingTime || unloadDefault) * 60_000;
    }
  }

  return { timeline, conflicts, hasConflicts: conflicts.length > 0 };

  // ── Helpers ─────────────────────────────────────────────────────────
  function pushEvent(ot, kind, timeMs, status) {
    const wFrom = kind === 'pickup' ? ot.pickupWindowFrom : ot.deliveryWindowFrom;
    const wTo   = kind === 'pickup' ? ot.pickupWindowTo   : ot.deliveryWindowTo;
    const event = {
      otId: ot.id || ot._id,
      orderNumber: ot.orderNumber,
      kind,
      time: new Date(timeMs),
      windowFrom: wFrom ? new Date(wFrom) : null,
      windowTo: wTo ? new Date(wTo) : null,
      status, // 'ok' | 'early' | 'late'
      // Carry SLA metadata so scoreSim can penalise sequences where urgent
      // OTs are visited late — without this, brute-force only minimises span.
      slaHours: ot.slaHours,
      createdAt: ot.createdAt,
    };
    timeline.push(event);
    if (status === 'late') {
      conflicts.push({
        otId: event.otId,
        orderNumber: event.orderNumber,
        kind,
        predictedAt: event.time,
        windowTo: event.windowTo,
      });
    }
  }
}

function checkWindow(timeMs, windowFrom, windowTo) {
  const from = windowFrom ? new Date(windowFrom).getTime() : null;
  const to   = windowTo   ? new Date(windowTo).getTime()   : null;
  if (to != null && timeMs > to) return 'late';
  if (from != null && timeMs < from) return 'early';
  return 'ok';
}

function isSamePoint(lat1, lng1, lat2, lng2, epsDeg = 0.0001) {
  return Math.abs(lat1 - lat2) < epsDeg && Math.abs(lng1 - lng2) < epsDeg;
}

function factorial(n) {
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}

function* permutations(arr) {
  if (arr.length <= 1) { yield arr.slice(); return; }
  for (let i = 0; i < arr.length; i++) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    for (const p of permutations(rest)) yield [arr[i], ...p];
  }
}

// Score helper: combined objective of total span + SLA-lateness weight.
// Lower is better. Used by both the brute-force tiebreak and the NN/2-opt loop.
function scoreSim(sim) {
  if (!sim.timeline || sim.timeline.length === 0) return Infinity;
  const span = sim.timeline[sim.timeline.length - 1].time - sim.timeline[0].time;

  // Layered penalty system — each tier dominates the next so the algorithm
  // optimises in a strict priority order:
  //
  //   1. CONFLICTS         — pickup/delivery outside its window (hard fail)
  //   2. SLA TIER          — every "less-urgent before more-urgent" pair
  //   3. WINDOW PRECEDENCE — within same tier, earlier window first
  //   4. SPAN              — geography + traffic (HERE matrix when available)
  //
  // Magnitudes are chosen so even a single inversion at one tier costs more
  // than any realistic improvement at the next: 1e10 > 1e8 > 1e7 > span (~1e6).
  const CONFLICT_PENALTY        = 1e10;
  const SLA_INVERSION_PENALTY   = 1e8;
  const WINDOW_INVERSION_PENALTY = 1e7;

  const conflicts = (sim.conflicts?.length || 0) * CONFLICT_PENALTY;

  let slaInversions = 0;
  let windowInversions = 0;
  const deliveries = sim.timeline.filter(ev => ev.kind === 'delivery');
  const slaOf = (ev) => ev?.slaHours != null && ev.slaHours !== '' ? Number(ev.slaHours) : Infinity;
  const winOf = (ev) => ev?.windowFrom ? ev.windowFrom.getTime?.() ?? new Date(ev.windowFrom).getTime() : Infinity;
  for (let i = 0; i < deliveries.length; i++) {
    const a = deliveries[i];
    for (let j = i + 1; j < deliveries.length; j++) {
      const b = deliveries[j];
      const slaA = slaOf(a);
      const slaB = slaOf(b);
      if (slaA > slaB) {
        slaInversions++;
      } else if (slaA === slaB) {
        // Same SLA tier — fall through to window precedence.
        const winA = winOf(a);
        const winB = winOf(b);
        if (winA > winB) windowInversions++;
      }
    }
  }

  return span
    + conflicts
    + slaInversions * SLA_INVERSION_PENALTY
    + windowInversions * WINDOW_INVERSION_PENALTY;
}

// Nearest-neighbour construction ordered by the driver's home (if given) or
// the first OT's pickup. At each step, picks the next OT that minimises a
// weighted cost: travel time to it + SLA urgency penalty (tighter deadline
// gets a bonus). Works for any N and always produces a sequence.
function nearestNeighbourOrder(orders, opts) {
  const now = Date.now();
  // Lateness penalty (kept as a soft guard — pushes the algorithm away from
  // arrivals past the SLA deadline). Tier filtering below handles precedence.
  const slaPenalty = (o, arrivalMs) => {
    if (o.slaHours == null || o.slaHours === '') return 0;
    const created = o.createdAt ? new Date(o.createdAt).getTime() : now;
    const deadline = created + Number(o.slaHours) * 3600_000;
    const lateMin = Math.max(0, (arrivalMs - deadline) / 60_000);
    return lateMin * 120;
  };
  const slaTierOf = (o) => o?.slaHours != null && o.slaHours !== '' ? Number(o.slaHours) : Infinity;

  const remaining = [...orders];
  const result = [];
  // Starting point: driverHome if provided, else first pickup
  const travelProvider = opts.travelProvider
    || ((lat1, lng1, lat2, lng2) => {
      const km = haversineKm(lat1, lng1, lat2, lng2);
      return { seconds: (km / 60) * 3600, km };
    });
  let curLat, curLng;
  if (opts.driverHome?.lat && opts.driverHome?.lng) {
    curLat = Number(opts.driverHome.lat);
    curLng = Number(opts.driverHome.lng);
  } else {
    const firstPk = pickupCoords(remaining[0]);
    curLat = firstPk.lat; curLng = firstPk.lng;
  }

  // Track elapsed time roughly so SLA penalties evolve as the route unfolds
  let elapsedMs = Date.now();

  while (remaining.length > 0) {
    // Tiered precedence: at each step only pick among OTs whose `slaHours`
    // equals the smallest still in the queue. So all 1h-SLA OTs go first,
    // then all 2h, then 4h, etc. Within a tier, we then prefer the OT with
    // the earliest `deliveryWindowFrom`; ties break on travel time.
    let minSla = Infinity;
    for (const o of remaining) {
      const t = slaTierOf(o);
      if (t < minSla) minSla = t;
    }
    let bestIdx = 0, bestCost = Infinity, bestWin = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const ot = remaining[i];
      if (slaTierOf(ot) > minSla) continue;
      const dl = deliveryCoords(ot);
      if (!dl.lat || !dl.lng) continue;
      const leg = travelProvider(curLat, curLng, dl.lat, dl.lng);
      const arrivalMs = elapsedMs + leg.seconds * 1000;
      const cost = leg.seconds + slaPenalty(ot, arrivalMs);
      const winMs = ot.deliveryWindowFrom ? new Date(ot.deliveryWindowFrom).getTime() : Infinity;
      // Prefer earlier window; on equal window, lower cost wins.
      const isBetter = winMs < bestWin
        || (winMs === bestWin && cost < bestCost);
      if (isBetter) { bestCost = cost; bestWin = winMs; bestIdx = i; }
    }
    const picked = remaining.splice(bestIdx, 1)[0];
    const dl = deliveryCoords(picked);
    const leg = travelProvider(curLat, curLng, dl.lat, dl.lng);
    elapsedMs += leg.seconds * 1000 + (picked.unloadingTime || 30) * 60_000;
    curLat = dl.lat; curLng = dl.lng;
    result.push(picked);
  }
  return result;
}

// 2-opt local search: for each pair (i, j) with i < j, try reversing the
// segment between them. Keeps the swap if the simulation score improves.
// Converges quickly for N ≤ 15 (typical ≤ 100 iterations).
function twoOpt(orders, opts) {
  if (orders.length < 4) return orders;
  let best = orders.slice();
  let bestScore = scoreSim(simulate(best, opts));
  let improved = true;
  let iterations = 0;
  const maxIterations = 300;
  // When `pinFirst` is true the optimiser is not allowed to move the first
  // OT out of position 0 — used when the operator pinned a specific OT as
  // the starting stop in the bulk-assign dialog.
  const startI = opts?.pinFirst ? 1 : 0;

  while (improved && iterations < maxIterations) {
    improved = false;
    iterations++;
    for (let i = startI; i < best.length - 1; i++) {
      for (let j = i + 1; j < best.length; j++) {
        const candidate = best.slice();
        // Reverse the segment [i, j]
        const segment = candidate.slice(i, j + 1).reverse();
        candidate.splice(i, segment.length, ...segment);
        const score = scoreSim(simulate(candidate, opts));
        if (score < bestScore - 1) { // 1ms threshold to avoid thrash
          best = candidate;
          bestScore = score;
          improved = true;
        }
      }
    }
  }
  return best;
}

/**
 * Try to find the best execution order for a consolidated route.
 * Strategy depends on problem size:
 * - ≤6 OTs: brute-force all permutations, pick the best conflict-free solution
 *   (tiebreak by total span).
 * - 7–12 OTs: nearest-neighbour construction (SLA-weighted) + 2-opt improvement.
 *   Typical time: <200 ms for 12 OTs with cached HERE matrix.
 * - >12 OTs: same NN + 2-opt — still scales acceptably.
 *
 * Returns the same shape as simulate() plus { reorderedOrders }.
 */
function findBestOrder(orders, opts = {}) {
  if (!Array.isArray(orders) || orders.length < 2) {
    return { ...simulate(orders, opts), reorderedOrders: orders };
  }

  // Pin one OT as the first stop when requested (operator picked an OT in
  // the departure dropdown of the bulk-assign dialog). The remaining OTs
  // are still optimised; the pinned one stays at position 0.
  const firstStopId = opts.firstStopOTId;
  let pinned = null;
  let workingOrders = orders;
  if (firstStopId != null) {
    const idx = orders.findIndex(o => String(o.id || o._id) === String(firstStopId));
    if (idx >= 0) {
      pinned = orders[idx];
      workingOrders = orders.filter((_, i) => i !== idx);
    }
  }
  // Optimiser opts pass `pinFirst: true` to the 2-opt loop so it doesn't
  // swap position 0 after we prepend the pinned OT.
  const optsForOptim = pinned ? { ...opts, pinFirst: true } : opts;

  // Trivial case after pinning
  if (workingOrders.length === 0 && pinned) {
    return { ...simulate([pinned], opts), reorderedOrders: [pinned] };
  }
  if (workingOrders.length === 1 && pinned) {
    const finalOrder = [pinned, ...workingOrders];
    return { ...simulate(finalOrder, opts), reorderedOrders: finalOrder };
  }

  // Small problem — exact brute force is fast (720 perms for 6)
  if (workingOrders.length <= 6) {
    let best = null;
    for (const perm of permutations(workingOrders)) {
      const candidate = pinned ? [pinned, ...perm] : perm;
      const sim = simulate(candidate, opts);
      const score = scoreSim(sim);
      if (!best || score < best.score) {
        best = { perm: candidate, sim, score };
      }
    }
    if (best) return { ...best.sim, reorderedOrders: best.perm };
    const fallback = pinned ? [pinned, ...workingOrders] : workingOrders;
    return { ...simulate(fallback, opts), reorderedOrders: fallback };
  }

  // Larger problem — heuristic NN + 2-opt (pinned stays at index 0)
  const seedRest = nearestNeighbourOrder(workingOrders, opts);
  const seed = pinned ? [pinned, ...seedRest] : seedRest;
  const optimised = twoOpt(seed, optsForOptim);
  return { ...simulate(optimised, opts), reorderedOrders: optimised };
}

function validateConsolidation(orders, opts = {}) {
  // Two-stage:
  //   1. Seed order by SLA deadline (tightest first), then delivery window —
  //      matches the Petrotec workflow priority.
  //   2. Run the VRP optimiser (findBestOrder) so the algorithm decides the
  //      truly best sequence given travel matrix + time windows + SLAs.
  const now = Date.now();
  const deadlineMs = (o) => {
    if (o.slaHours != null && o.slaHours !== '') {
      const created = o.createdAt ? new Date(o.createdAt).getTime() : now;
      return created + (Number(o.slaHours) * 3600_000);
    }
    return null;
  };
  const seeded = [...orders].sort((a, b) => {
    const da = deadlineMs(a);
    const db = deadlineMs(b);
    if (da != null && db != null) return da - db;
    if (da != null) return -1;
    if (db != null) return 1;
    const ta = new Date(a.deliveryWindowFrom || a.pickupWindowFrom || 0).getTime();
    const tb = new Date(b.deliveryWindowFrom || b.pickupWindowFrom || 0).getTime();
    return ta - tb;
  });
  // Run the full optimiser (brute-force ≤6, NN+2-opt otherwise).
  // `opts.firstStopOTId` propagates through findBestOrder so the operator
  // can pin a specific OT as the route's starting point.
  const best = findBestOrder(seeded, opts);
  return { ...best, orderedOrders: best.reorderedOrders || seeded };
}

export {
  validateConsolidation,
  findBestOrder,
  simulate,
  collectUniquePoints,
  makeMatrixProvider,
};
